import { useNavigate, useParams } from 'react-router-dom';
import { isPathMode } from '../config/markets';

/**
 * useMarketNavigate — slug-aware navigation hook.
 *
 * In path-mode (OTC on offtheclockspots.com), prepends /:marketSlug to absolute paths.
 * In env-mode (TCC), behaves exactly like useNavigate().
 *
 * Usage:
 *   const navigate = useMarketNavigate();
 *   navigate('/login');          // → /naperville/login (path-mode) or /login (env-mode)
 *   navigate('/admin');          // → /naperville/admin (path-mode) or /admin (env-mode)
 *   navigate('relative-path');   // unchanged in both modes (no leading /)
 */
export function useMarketNavigate() {
  const navigate = useNavigate();
  const params = useParams();
  const slug = params.marketSlug;

  return (path, options) => {
    if (isPathMode() && slug && typeof path === 'string' && path.startsWith('/')) {
      navigate(`/${slug}${path}`, options);
    } else {
      navigate(path, options);
    }
  };
}

/**
 * Helper to resolve a path with the market slug prefix.
 * Useful for cases where you need the resolved string (e.g. <Navigate to={...}>)
 * rather than calling navigate().
 */
export function resolveMarketPath(path, marketSlug) {
  if (isPathMode() && marketSlug && typeof path === 'string' && path.startsWith('/')) {
    return `/${marketSlug}${path}`;
  }
  return path;
}
